<script>
  import settings from '../config';
  import session from '../lib/session';
  import Dialog from '../lib/components/Dialog.svelte';
  import APIClient from '../state-charts/api-client';
  import { t9n } from '../lib/translate.js';
  import { TS } from '../config/translation-keys.js';
  import { show_message } from '../lib/flash.ts';
  import { is_debug } from '../devtools';

  import AddCardDialog from './AddCardDialog.svelte';

  // component options  ------------------------------------------------------ //

  export let route_name;

  // internal variables ------------------------------------------------------ //

  let payment_provider;
  let add_card_dialog;
  let removal_confirm_dialog;

  let payment_options = null;
  let cards = null;
  let billing = null;
  let pp = null;
  let card_id = null;

  let options_is_loading = true;
  let is_seita;
  let is_nets;

  let card_init_flag = false;

  // translations ------------------------------------------------------------ //

  let explanation = t9n(TS.CARDS_LABEL_TOOLTIP);
  let form_title = t9n(TS.CARDS_FORM_TITLE);
  let options_loading_text = t9n(TS.CARDS_OPTIONS_LOADING_TEXT);
  let list_cc_label = t9n(TS.CARDS_LIST_CREDIT_CARD_LABEL);
  let list_exp_label = t9n(TS.CARDS_LIST_EXPIRES_LABEL);
  let list_remove_label = t9n(TS.CARDS_LIST_REMOVE_LABEL);
  let list_pp_label = t9n(TS.CARDS_LIST_PP_LABEL);
  let list_billing_label = t9n(TS.CARDS_LIST_BILLING_LABEL);
  let dialog_opener_label = t9n(TS.CARDS_ADD_CARD_LABEL);

  let remove_confirm_title = t9n(TS.CARDS_REMOVE_CONFIRM_TITLE);
  let remove_confirm_confirm_label = t9n(TS.CARDS_REMOVE_CONFIRM_CONFIRM_LABEL);
  let remove_confirm_cancel_label = t9n(TS.CARDS_REMOVE_CONFIRM_CANCEL_LABEL);
  let remove_confirm_content_text = t9n(TS.CARDS_REMOVE_CONFIRM_CONTENT_TEXT);
  let remove_success_text = t9n(TS.CARDS_REMOVE_SUCCESS_TEXT);
  let menu_label_pilot_points = t9n(TS.MENU_LABEL_PILOT_POINTS);

  // utility functions ------------------------------------------------------- //

  const open_add_card_dialog = e => {
    let target = document.querySelector('.addcard-dialog');
    target.innerHTML = '';
    let dialog = new AddCardDialog({
      target: target,
      props: { add_card: add_card },
    });
    dialog.open();
  };

  const confirmed_removal = async () => {
    removal_confirm_dialog.close();

    try {
      await APIClient.remove_card({ credit_card_id: card_id });
      await load_payment_options();
      show_message(remove_success_text, 'success');
    } catch (e) {
      console.log(e);
    }
  };

  const create_removal_confirm_dialog = () => {
    let target = document.querySelector('.dialog');

    target.innerHTML = '';

    let dialog = new Dialog({
      target: target,
      props: {
        id: 'card-removal-confirm',
        title: remove_confirm_title,
        content: remove_confirm_content_text,
        modal: true,
        buttons: [
          {
            label: remove_confirm_cancel_label,
            action: () => {
              dialog.close();
            },
            classes: [],
          },
          {
            label: remove_confirm_confirm_label,
            action: async () => { await confirmed_removal() },
            classes: [],
          },
        ],
      },
    });

    return dialog;
  };

  const create_in_progress_dialog = () => {
    let target = document.querySelector('.dialog');

    target.innerHTML = '';

    let dialog = new Dialog({
      target: target,
      props: {
        id: 'card-removal-confirm',
        title: t9n(TS.LABEL_WARNING),
        content: t9n(TS.CARDS_LABEL_USED),
        modal: true,
        buttons: [
          {
            label: remove_confirm_cancel_label,
            action: () => {
              dialog.close();
            },
            classes: [],
          },
          {
            label: t9n(TS.LABEL_CONTINUE),
            action: open_removal_dialog,
            classes: [],
          },
        ],
      },
    });

    return dialog;
  };

  const sort_payment_options = () => {
    cards = payment_options.filter(
      o => o.payment_type === 'payment_type_credit_card'
    );
    billing = payment_options.find(
      o => o.payment_type === 'payment_type_billing'
    );
    pp = payment_options.find(
      o => o.payment_type === 'payment_type_pilot_points'
    );
  };

  const load_payment_options = async () => {
    try {
      let response = await APIClient.get_payment_options({ price: 0.0 });

      payment_options = response.options;
      sort_payment_options();
      options_is_loading = false;
    } catch (e) {
      console.log(e);
    }
  };

  const init_card = async () => {
    try {
      let results = await APIClient.init_card();
      return results.reference;
    } catch (e) {
      if (is_debug()) console.log(e);
    }
  };

  const add_card = async () => {
    await load_payment_options();
  };

  const remove_card = async e => {
    card_id = e.target.dataset.card_id;
    let active_orders = get_card_orders(card_id);

    if (!!active_orders) {
      if (active_orders.in_progress > 0 || active_orders.initialized > 0) {
        open_in_progress_dialog();
      } else {
        open_removal_dialog();
      }
    } else {
      open_removal_dialog();
    }
  };

  const open_in_progress_dialog = () => {
    removal_confirm_dialog = create_in_progress_dialog();
    removal_confirm_dialog.open();
  };

  const open_removal_dialog = () => {
    removal_confirm_dialog = create_removal_confirm_dialog();
    removal_confirm_dialog.open();
  };

  const get_card_orders = id => {
    let return_val = null;

    for (let i = 0; i < payment_options.length; i++) {
      if (!!payment_options[i].data) {
        if (!!payment_options[i].data.credit_card) {
          if (
            Number(id) === payment_options[i].data.credit_card.credit_card_id
          ) {
            if (!!payment_options[i].data.credit_card.orders) {
              return_val = payment_options[i].data.credit_card.orders;
            }
          }
        }
      }
    }
    return return_val;
  };

  const waiting_for_nets_response = async transaction_id => {
    const result_arrived = setInterval(async () => {
      let result;
      let content = null;

      try {
        content = document
          .getElementById('nets_form')
          .contentDocument.getElementsByTagName('body')[0]
          .getElementsByTagName('pre')[0].innerHTML;
      } catch (e) {
        // no-op
        content = null;
      }

      if (content !== null) {
        document.getElementById('add_card_dialog_content').style.opacity =
          '0.1';
        clearTimeout(result_arrived);
        result = JSON.parse(content);

        if (result.responseCode === 'OK') {
          document.getElementById('nets_form').style.display = 'none';
          // add_card_dialog.close();
          await add_card(transaction_id);
        } else if (result.responseCode === 'Cancel') {
          add_card_dialog.close();
          // init_nets_form();
        }
      }
    }, 1000);
  };

  const init_nets_form = async () => {
    // ***same as AddCardDialog
    const transaction_id = await init_card();
    const container = document.querySelector('#add_card_dialog_content');

    // ***same as AddCardDialog
    const nets_url = `${settings.nets_host}/Terminal/default.aspx?merchantId=619040&transactionId=${transaction_id}`;
    const iframe = document.createElement('iframe');

    // **** this line differs!!!! :
    container.setAttribute('data-transaction_id', transaction_id);

    iframe.setAttribute('id', 'nets_form');
    if (window.screen.availWidth >= 768) {
      iframe.setAttribute('style', 'min-height: 350px; min-width: 550px');
    } else {
      iframe.setAttribute('style', 'min-height: 350px; min-width: 0');
    }
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute('src', nets_url);

    if (container) {
      container.innerHTML = '';
      container.append(iframe);
    }
    // ***sameness ends

    await waiting_for_nets_response(transaction_id);
  };

  const init_cards = async () => {
    document
      .querySelector('.mdc-top-app-bar--fixed-adjust')
      .scrollIntoView({ behavior: 'smooth' });
    await load_payment_options();
    is_seita = $session.merchant.payment_provider === 'seita';
    is_nets = $session.merchant.payment_provider === 'nets';
    payment_provider = $session.merchant.payment_provider;

    if (is_nets) {
      // init_nets_form();
    } else if (is_seita) {
      const card_number_field = document.querySelector('.card-number');
      if (card_number_field) {
        card_number_field.mask('0000 0000 0000 0000', { reverse: true });
      }
    }

    card_init_flag = true;
  };

  if (!!$session.merchant && !card_init_flag) {
    (async () => {
      await init_cards();
    })();

  }

  $: options_is_loading = !!payment_options;
</script>
<!-- Heading -->
<div id="cards_heading">
  <div style="text-align: center;">
    <h1 style="margin-bottom: 25px" class="mdc-typography--headline5">
      {form_title}
    </h1>
  </div>
</div>

<!-- Help line -->
{#if $session.merchant.payment_provider !== 'None'}
  <div
    id="help-liner"
    class="fade-in-long"
    style="margin-bottom: 7px; margin-top: -11px;">
    <div class="tooltip-default" style="text-align: center">
      <span id="tooltip_def" class="tooltip-text tooltip-top">
        {explanation}
      </span>
    </div>
  </div>
{/if}

<!-- Here is the table for cards -->
<div id="cards_panel">
  <table id="payment_methods" class="table">
    <!-- Loading caption -->
    <tr class:hidden={!!options_is_loading}>
      <td style="border-bottom: 0" />
      <td style="border-bottom: 0" class="label">{options_loading_text}</td>
      <td style="border-bottom: 0" />
    </tr>

    <!-- Cards loop -->
    {#if !!cards}
      {#each cards as card}
        <tr>
          <td colspan="2">
            <h6>{list_cc_label}:</h6>
            <br />
            <b>
              {card.data.credit_card.credit_card_number} ({card.data.credit_card.provider})
            </b>
          </td>
          <td colspan="1" class="mobile-bottom">
            <h6>{list_exp_label}:</h6>
            <br />
            <b>
              {card.data.credit_card.expiry_year}/{card.data.credit_card.expiry_month}
            </b>
          </td>
          <td style="vertical-align: bottom; text-align: right">
            <button
              class="mdc-button hover"
              style="color: #bb0000;"
              data-card_id={card.data.credit_card.credit_card_id}
              on:click={remove_card}>
              {list_remove_label}
            </button>
          </td>
        </tr>
      {/each}
    {/if}

    {#if !!pp}
      <tr>
        <td colspan="2">
          <h6>{list_pp_label}</h6>
          <br />
          <b>{pp.data.pilot_points.amount}</b>
        </td>
        <td />
        <td />
      </tr>
    {/if}

    <!-- Billing -->
    {#if !!billing}
      <tr>
        <td style="height: 57px;" colspan="2">{list_billing_label}</td>
        <td />
        <td />
      </tr>
    {/if}
  </table>

  <!-- Add cards button -->
  <div style="display: flex; justify-content: center;">
    <div style="text-align: center">
      {#if payment_provider !== "None"}
      <button
        data-cy="add_card_button"
        style="margin-right: 10px"
        class="mdc-button mdc-button--raised"
        on:click|stopPropagation|preventDefault={open_add_card_dialog}>
        {dialog_opener_label}
      </button>
      {/if}
    </div>

    <div style="text-align: center">
      <a
        data-link={true}
        href={'/pilot-points'}
        data-cy="open_pp_button"
        style="margin-right: 10px"
        class="mdc-button mdc-button--raised">
        {menu_label_pilot_points}
      </a>
    </div>
  </div>

</div>

<div class="dialog" />
<div class="addcard-dialog" />
<!--</main>-->
<!--</AuthenticatedLayout>-->

<style>
  table {
    width: 100%;
    margin-bottom: 35px;
    background: #eef0f0;
    line-height: 2.5;
    /*padding-left: 15px;*/
    border-spacing: 0;
  }

  td {
    border-bottom: 1px solid #b7b7b7;
  }

  h6 {
    font-weight: normal;
    margin-bottom: -50px;
    margin-top: 1px;
  }

  #payment_methods td {
    /*border: none;*/
    color: #7a7676;
    padding-left: 15px;
    border-bottom: 1px solid #b7b7b7;
  }

  .label {
    text-align: right;
  }

  .hover:hover {
    background: #f4d1d14d;
    border-radius: 7px;
    height: 33px;
  }

  .hidden {
    display: none;
  }

  /* This override is for the Nets iframe to fit */
  .mdc-dialog .mdc-dialog__surface {
    max-width: 100%;
  }

  /* Tooltip
  *******************************************************/

  .tooltip-default {
    position: relative;
    font-size: 12px;
    /*top: -60px;*/
    animation: transition_in 0.6s;
  }

  .tooltip-default .tooltip-top {
    /*position: absolute;*/
    z-index: 1;
    bottom: 100%;
    right: 0;
    left: 50%;
  }

  .tooltip-default .tooltip-text {
    background-color: var(--mdc-theme-secondary);
    color: #fff;
    text-align: center;
    border-radius: 6px;
    padding: 8px 8px;
    padding-left: 30px;
    padding-right: 30px;
    font-size: 13px;
    box-shadow: 1px 1px 3px 0px rgba(0, 0, 0, 0.3);
    display: block;
  }

  .tooltip-default .tooltip-text::after {
    content: ' ';
    position: relative;
    top: 30px;
    left: -125px;
    margin-left: -5px;
    border-width: 5px;
    border-style: solid;
    border-color: #018786 transparent transparent transparent;
  }

  /* Desktop Mode
  *******************************************************/
  @media (min-width: 1025px) {
    .wrapper {
      width: 600px;
      margin: 0 auto;
      height: fit-content;
    }
  }

  /* Tablet Mode
  *******************************************************/
  @media (min-width: 769px) and (max-width: 1024px) {
    .wrapper {
      width: 70%;
      margin: 0 auto;
      height: fit-content;
    }
  }

  /* Mobile Mode
  *******************************************************/
  @media (min-width: 0) and (max-width: 768px) {
    .wrapper {
      margin: 0 auto;
      height: fit-content;
      margin-left: 50px;
      margin-bottom: 50px;
    }

    #cards_panel {
      overflow-x: auto;
    }

    .mobile-bottom {
      vertical-align: bottom;
    }
  }
</style>
